import React, { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import PeopleIcon from '@mui/icons-material/People';
import EloBadge from "../common/EloBadge.js";

const useStyles = makeStyles((theme) => ({
  dialog: {
    position: 'fixed',
    inset: 0,
    zIndex: 1301,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .MuiDialog-container': {
      alignItems: 'center',
      justifyContent: 'center',
    },
    '& .MuiDialog-paper': {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      background: 'linear-gradient(135deg, #1C1E20 0%, #2A2D30 100%)',
      borderRadius: '20px',
      border: '1px solid rgba(232, 163, 61, 0.15)',
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)',
      width: '22rem',
      margin: 0,
      [theme.breakpoints.up("xl")]: {
        width: '26.5rem',
      },
    }
  },
  dialogContent: {
    padding: '1.4rem',
    textAlign: 'center',
    [theme.breakpoints.up("xl")]: {
      padding: '1.6rem',
    },
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '1rem',
    gap: '0.8rem',
  },
  inviteIcon: {
    fontSize: '2.5rem',
    color: '#E8A33D',
    filter: 'drop-shadow(0 0 15px rgba(232, 163, 61, 0.4))',
    [theme.breakpoints.up("xl")]: {
      fontSize: '2.75rem',
    },
  },
  title: {
    color: '#ffffff',
    fontSize: '1.3rem',
    fontWeight: 600,
    letterSpacing: '0.5px',
    [theme.breakpoints.up("xl")]: {
      fontSize: '1.5rem',
    },
  },
  countdown: {
    color: '#E8A33D',
    fontSize: '0.9rem',
    fontWeight: 500,
    background: 'rgba(232, 163, 61, 0.1)',
    width: '2rem',
    padding: '0.3rem 0.8rem',
    borderRadius: '12px',
    border: '1px solid rgba(232, 163, 61, 0.2)',
    marginLeft: 'auto',
    fontFamily: 'Roboto',
    [theme.breakpoints.up("xl")]: {
      fontSize: '1.1rem',
      padding: '0.4rem 1.1rem',
      borderRadius: '16px',
    },
  },
  friendInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '0.5rem',
    background: 'rgba(255, 255, 255, 0.03)',
    borderRadius: '16px',
    marginBottom: '1rem',
    border: '1px solid rgba(232, 163, 61, 0.1)',
    backdropFilter: 'blur(10px)',
    [theme.breakpoints.up("xl")]: {
      padding: '0.8rem',
    },
  },
  avatar: {
    width: '3rem',
    height: '3rem',
    border: '2px solid rgba(232, 163, 61, 0.3)',
    [theme.breakpoints.up("xl")]: {
      width: '3.5rem',
      height: '3.5rem',
    },
  },
  friendDetails: {
    textAlign: 'left',
    flex: 1,
  },
  friendName: {
    color: '#ffffff',
    fontSize: '1.1rem',
    fontWeight: 600,
    marginBottom: '0.4rem',
    [theme.breakpoints.up("xl")]: {
      fontSize: '1.3rem',
    },
  },
  eloContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  actions: {
    display: 'flex',
    gap: '0.8rem',
    justifyContent: 'center',
  },
  acceptButton: {
    background: 'linear-gradient(135deg, #38b000 0%, #2d8f00 100%)',
    color: '#ffffff',
    padding: '0.6rem 2.2rem',
    borderRadius: '12px',
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '0.9rem',
    border: 'none',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #2d8f00 0%, #38b000 100%)',
      transform: 'translateY(-2px)',
    },
    [theme.breakpoints.up("xl")]: {
      padding: '0.7rem 2.5rem',
      fontSize: '1.2rem',
    },
  },
  declineButton: {
    background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
    color: '#ffffff',
    padding: '0.6rem 2.2rem',
    borderRadius: '12px',
    fontWeight: 600,
    textTransform: 'none',
    fontSize: '0.9rem',
    border: 'none',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)',
      transform: 'translateY(-2px)',
    },
    [theme.breakpoints.up("xl")]: {
      padding: '0.7rem 2.5rem',
      fontSize: '1.2rem',
    },
  },
}));

export default function InviteDialog({ 
  open, 
  inviteData, 
  onDecline,  
  onAccept,
  countdown = 60000,
}) {
  const classes = useStyles();
  const [timeLeft, setTimeLeft] = useState(countdown / 1000);

  useEffect(() => {
    if (open && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => Math.max(0, prev - 1));
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [open, timeLeft]);

  useEffect(() => {
    if (open) {
      setTimeLeft(countdown / 1000);
    }
  }, [open, countdown]);

  if (!inviteData) return null;

  const handleDecline = () => {
    onDecline(inviteData.inviteId, inviteData);
  };

  const handleAccept = () => {
    onAccept(inviteData.inviteId, inviteData);
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          onDecline(inviteData.inviteId, inviteData);
        }
      }}
      className={classes.dialog}
      maxWidth="sm"
      fullWidth
      BackdropProps={{
        style: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
        }
      }}
    >
        <DialogContent className={classes.dialogContent}>
          <div className={classes.header}>
            <PeopleIcon className={classes.inviteIcon} />
            <Typography className={classes.title}>
              Team Invite
            </Typography>
            <div className={classes.countdown}>
              {timeLeft}s
            </div>
          </div>

          <div className={classes.friendInfo}>
            <Avatar
              className={classes.avatar}
              src={inviteData.fromUserPicture}
              alt={inviteData.fromUserName}
            />
            <div className={classes.friendDetails}>
              <Typography className={classes.friendName}>
                {inviteData.fromUserName}
              </Typography>
              <div className={classes.eloContainer}>
                <EloBadge value={inviteData.fromUserElo} size="small" />
              </div>
            </div>
          </div>

          <DialogActions className={classes.actions}>
            <Button
              className={classes.declineButton}
              onClick={handleDecline}
            >
              Decline
            </Button>
            <Button
              className={classes.acceptButton}
              onClick={handleAccept}
            >
              Accept
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    );
}
